import type { CSSProperties } from 'react';
import { TITLE_SKY, titleLayers, type ArtImage } from '../assets';

/**
 * The storyboard's landing composition, built from the four delivered layers
 * rather than a flat plate: sky and sea behind, the sea stacks on the waterline,
 * the cast in front, gulls over the top.
 *
 * Every layer hangs off one number — `--water`, the horizon's position in the
 * viewport. Move that and the whole composition moves together, the background
 * plate included. Nothing here is positioned against the frame directly.
 *
 * Three regimes, and the middle one is why this is not simply two:
 *
 *  - phone: the plate fills the frame, the cast is centred under the copy.
 *  - 768-1280: the copy takes the left column, and there is genuinely not room
 *    for a full-height cast beside it — at 768 a 448px column leaves about
 *    250px. So the cast shrinks (sized off viewport WIDTH, the scarce axis) and
 *    moves right, and the horizon rises so the smaller composition sits centred
 *    instead of stranded at the bottom under a slab of empty sky.
 *  - 1280+: the cast clears the column at full height, so the horizon drops and
 *    the composition anchors to the bottom of the window.
 */

const WATER = 'var(--water)';

/**
 * Height of the background plate, solved rather than guessed.
 *
 * Three conditions have to hold at once, at any window aspect:
 *
 *  1. `water / 0.636` — tall enough that its horizon, which sits at 63.6% of
 *     its own height, can reach down to `--water` without the plate's top edge
 *     falling inside the frame.
 *  2. `(100% - water) / 0.364` — tall enough that the remainder still reaches
 *     the bottom of the window.
 *  3. `72vw` — tall enough that the box stays narrower in aspect than the plate
 *     itself (1600/1144). This one is not about coverage: object-cover silently
 *     switches between scaling on width and on height, and on a short wide
 *     window (1920x800) it flipped to width and put the horizon 160px off the
 *     line. Below this aspect it always scales on height, so the horizon is
 *     exactly 63.6% of the height and the arithmetic holds.
 *
 * An earlier attempt sized the plate to its natural width and painted flat bands
 * above and below it. The bands were sampled from its edge rows and still showed
 * as seams, because the sky is a gradient — there is no one colour that matches.
 * Covering the frame outright avoids the question.
 */
const PLATE_H =
  'max(calc(var(--water) / 0.636), calc((100% - var(--water)) / 0.364), 72vw)';

/**
 * How much of the cast cut-out's height sits BELOW the soles of their feet.
 *
 * The plate carries a faint reflection under the figures and then transparent
 * padding, so its image bottom is nowhere near where the legs end — measured off
 * the alpha channel, the solid figure stops at 88% of the height. Aligning the
 * image bottom to the waterline therefore left them hovering above the water.
 * Everything is positioned off the feet instead, with the inset scaled against
 * whatever height the cast is currently drawn at.
 */
const CAST_FOOT_INSET = 0.12;

/**
 * Horizon position per regime.
 *
 * The middle value tracks the viewport's aspect on purpose. The cast there is
 * sized in vw, so its height as a fraction of the FRAME changes with the window
 * — a fixed horizon would centre it at one width and strand it at another.
 * `47% + 16.7vw` is `47% + 0.44 x cast height`, which holds the figures'
 * midpoint on the frame's midline across the whole band.
 */
const WATER_BY_REGIME = '[--water:63.6%] md:[--water:calc(47%+16.7vw)] xl:[--water:89%]';

/**
 * Where the composition sits horizontally.
 *
 * `right` is the title screen: copy in the left column, art to its right.
 * `left` mirrors it for the result screen, where the recorded clip takes the
 * right edge and the art shares the left with the copy above it — so the
 * horizon drops near the foot of the window and the cast occupies the lower
 * half rather than the full height.
 *
 * Written as whole literal class strings because Tailwind only emits classes
 * whose text it can find in the source; assembling them would produce nothing.
 */
const PLACEMENT = {
  right: {
    water: WATER_BY_REGIME,
    rocks: 'left-[36%] h-[13%] md:left-[54%] md:h-[6.6vw] xl:left-[40%] xl:h-[16%]',
    cast: 'left-1/2 [--cast-h:68%] md:left-[82%] md:[--cast-h:38vw] xl:left-[68%] xl:[--cast-h:min(92%,63vw)]',
    gulls: 'left-1/2 h-[13%] md:left-[80%] md:h-[7vw] xl:left-[66%] xl:h-[17%]',
  },
  left: {
    water: '[--water:63.6%] lg:[--water:92%]',
    rocks: 'left-[36%] h-[13%] lg:left-[14%] lg:h-[10%]',
    cast: 'left-1/2 [--cast-h:68%] lg:left-[30%] lg:[--cast-h:55%]',
    gulls: 'left-1/2 h-[13%] lg:left-[28%] lg:h-[11%]',
  },
} as const;

export function TitleBackdrop({ align = 'right' }: { align?: keyof typeof PLACEMENT }) {
  const place = PLACEMENT[align];
  return (
    <div
      className={`absolute inset-0 overflow-hidden ${place.water}`}
      style={{ backgroundColor: TITLE_SKY }}
    >
      <Layer
        image={titleLayers.bg}
        className="absolute left-0 w-full max-w-none object-cover"
        style={{ height: PLATE_H, top: `calc(${WATER} - 0.636 * ${PLATE_H})` }}
        eager
      />

      {/*
        Sea stacks, behind everything, base carried below the horizon so they sit
        low against the cast. Sized off viewport height at the two ends and off
        width in the middle band, matching whichever axis the cast is using, so
        the composition keeps its internal proportions as it scales.
      */}
      <img
        src={titleLayers.rocks.fallback}
        alt=""
        aria-hidden
        className={`pointer-events-none absolute w-auto max-w-none -translate-x-1/2 -translate-y-full select-none ${place.rocks}`}
        style={{
          top: `calc(${WATER} + 7%)`,
          // The delivered cut-out ends in a dead straight horizontal cut. Below
          // the horizon that edge sits against the bright reflection band and
          // reads as a sticker, so the last fifth is faded into the water.
          maskImage: 'linear-gradient(to bottom, #000 76%, transparent 97%)',
          WebkitMaskImage: 'linear-gradient(to bottom, #000 76%, transparent 97%)',
        }}
      />

      {/*
        The cast. Centred on a phone with the copy beneath; right of the copy
        column at every desktop width.

        Phone height is 68% — as tall as it goes before the wingspan, 0.67 of the
        height, overruns a 390px frame. The middle band is sized in vw because
        width is what is scarce there. The top band clamps against width too:
        sizing purely off height meant a tall narrow window drew a proportionally
        wider cast, and at 1280x1200 the wings reached back over the copy. The
        clamp lives inside the variable, not on the height, because the foot
        inset is a fraction of --cast-h and would misalign against a height
        clamped separately.
      */}
      <img
        src={titleLayers.cast.fallback}
        alt=""
        aria-hidden
        className={`pointer-events-none absolute w-auto max-w-none -translate-x-1/2 -translate-y-full select-none object-contain object-bottom ${place.cast}`}
        style={{
          height: 'var(--cast-h)',
          // Feet 3% below the horizon, so they stand in the water rather than
          // on it.
          top: `calc(${WATER} + 3% + ${CAST_FOOT_INSET} * var(--cast-h))`,
        }}
      />

      {/*
        Gulls last, so they paint in FRONT of the cast — absolutely positioned
        siblings with no z-index, so source order is the stacking order. They
        track the cast's position and scale at each regime.
      */}
      <img
        src={titleLayers.gulls.fallback}
        alt=""
        aria-hidden
        className={`pointer-events-none absolute w-auto max-w-none -translate-x-1/2 -translate-y-full select-none ${place.gulls}`}
        style={{ top: `calc(${WATER} - 5%)` }}
      />
    </div>
  );
}

/** WebP with the derived fallback, without Artwork's full-bleed assumptions. */
function Layer({
  image,
  className,
  style,
  eager = false,
}: {
  image: ArtImage;
  className?: string;
  style?: CSSProperties;
  eager?: boolean;
}) {
  return (
    <picture>
      <source type="image/webp" srcSet={image.webp} />
      <img
        src={image.fallback}
        alt=""
        aria-hidden
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className={className}
        style={style}
      />
    </picture>
  );
}
